import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Typography, message, Modal, Form, Input, Table, Tag, Spin } from 'antd';
import { UploadOutlined, PlusOutlined, CopyOutlined, KeyOutlined, BookOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  
  h2 {
    margin-bottom: 8px !important;
    font-size: 24px !important;
    font-weight: 600 !important;
    color: ${COLORS.textPrimary} !important;
  }
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    
    .ant-card-head-title {
      color: ${COLORS.textPrimary} !important;
    }
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
`;

const ApiKeys: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [ipWhitelist, setIpWhitelist] = useState<any[]>([]);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [ipModalVisible, setIpModalVisible] = useState(false);
  const [switchWalletKeyModalVisible, setSwitchWalletKeyModalVisible] = useState(false);
  const [switchWalletPublicKey, setSwitchWalletPublicKey] = useState<string | null>(null);
  const [switchWalletKeyLoading, setSwitchWalletKeyLoading] = useState(false);
  const [myApiKeyModalVisible, setMyApiKeyModalVisible] = useState(false);
  const [webhookIPs, setWebhookIPs] = useState<string[]>([]);
  const [webhookIPsLoading, setWebhookIPsLoading] = useState(false);
  const [merchantCompanyId, setMerchantCompanyId] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [uploadForm] = Form.useForm();
  const [ipForm] = Form.useForm();
  const [editingIp, setEditingIp] = useState<any>(null);

  useEffect(() => {
    fetchPublicKey();
    fetchIpWhitelist();
    fetchMerchantCompanyId();
    fetchWebhookIPs();
  }, []);

  const fetchWebhookIPs = async () => {
    try {
      setWebhookIPsLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_WEBHOOK_IPS);
      if (response.data?.status === 'success' && response.data?.data) {
        setWebhookIPs(response.data.data.webhookIPs || []);
      }
    } catch (error: any) {
      console.error('Error fetching webhook IPs:', error);
    } finally {
      setWebhookIPsLoading(false);
    }
  };

  const fetchMerchantCompanyId = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_COMPANY_PROFILE);
      if (response.data?.status === 'success' && response.data?.data) {
        // Use merchantCompanyAppId (MD5 hash) instead of UUID
        if (response.data.data.profile) {
          const appId = response.data.data.profile.merchantCompanyAppId || null;
          setMerchantCompanyId(appId);
        }
        // Get API key from response
        if (response.data.data.apiKey) {
          setApiKey(response.data.data.apiKey);
        }
      }
    } catch (error: any) {
      console.error('Error fetching merchant company identifier:', error);
    }
  };

  const fetchPublicKey = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_PUBLIC_KEY);
      if (response.data?.status === 'success' && response.data?.data) {
        setPublicKey(response.data.data.publicKey || null);
      }
    } catch (error: any) {
      console.error('Error fetching public key:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIpWhitelist = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_API_IP_WHITELIST);
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setIpWhitelist(data.itemsList || data.items || []);
      }
    } catch (error: any) {
      console.error('Error fetching IP whitelist:', error);
    }
  };

  const fetchSwitchWalletPublicKey = async () => {
    try {
      setSwitchWalletKeyLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_SWITCH_WALLET_PUBLIC_KEY);
      if (response.data?.status === 'success' && response.data?.data?.publicKey) {
        setSwitchWalletPublicKey(response.data.data.publicKey);
      }
    } catch (error: any) {
      console.error('Error fetching Switch Wallet public key:', error);
      message.error('Failed to fetch Switch Wallet public key');
    } finally {
      setSwitchWalletKeyLoading(false);
    }
  };

  // Convert PEM to base64 (matching bootstrap.env format)
  const pemToBase64 = (pemKey: string) => {
    // Encode the entire PEM string to base64, remove newlines (matches generate.sh format)
    return btoa(pemKey).replace(/\s/g, '');
  };

  const handleOpenSwitchWalletKeyModal = () => {
    setSwitchWalletKeyModalVisible(true);
    if (!switchWalletPublicKey) {
      fetchSwitchWalletPublicKey();
    }
  };

  const handleUploadKey = async () => {
    try {
      const values = await uploadForm.validateFields();
      await apiClient.put(API_ENDPOINTS.MERCHANT_UPDATE_PUBLIC_KEY, {
        publicKey: values.publicKey,
      });
      message.success('RSA public key uploaded successfully');
      setUploadModalVisible(false);
      uploadForm.resetFields();
      fetchPublicKey();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to upload public key');
    }
  };

  const handleRemoveKey = async () => {
    try {
      await apiClient.delete(API_ENDPOINTS.MERCHANT_REMOVE_PUBLIC_KEY);
      message.success('RSA public key removed successfully');
      setPublicKey(null);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to remove public key');
    }
  };

  const handleAddIp = () => {
    setEditingIp(null);
    ipForm.resetFields();
    setIpModalVisible(true);
  };

  const handleEditIp = (ip: any) => {
    setEditingIp(ip);
    ipForm.setFieldsValue({
      ipAddress: ip.ip || ip.ipAddress, // Support both ip and ipAddress for backward compatibility
      description: ip.description,
    });
    setIpModalVisible(true);
  };

  const handleSaveIp = async () => {
    try {
      const values = await ipForm.validateFields();
      // Map ipAddress to ip for API
      const payload = {
        ip: values.ipAddress,
        description: values.description,
      };
      if (editingIp) {
        await apiClient.put(`${API_ENDPOINTS.MERCHANT_UPDATE_API_IP_WHITELIST_ENTRY}/${editingIp.id}`, payload);
        message.success('IP address updated successfully');
      } else {
        await apiClient.post(API_ENDPOINTS.MERCHANT_CREATE_API_IP_WHITELIST, payload);
        message.success('IP address added successfully');
      }
      setIpModalVisible(false);
      ipForm.resetFields();
      fetchIpWhitelist();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to save IP address');
    }
  };

  const handleDeleteIp = async (id: string) => {
    try {
      await apiClient.delete(`${API_ENDPOINTS.MERCHANT_DELETE_API_IP_WHITELIST_ENTRY}/${id}`);
      message.success('IP address deleted successfully');
      fetchIpWhitelist();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete IP address');
    }
  };

  const ipColumns = [
    {
      title: 'IP Address',
      dataIndex: 'ip',
      key: 'ip',
      render: (text: string, record: any) => text || record.ipAddress, // Support both ip and ipAddress
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'ACTIVE' ? 'green' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" onClick={() => handleEditIp(record)}>Edit</Button>
          <Button size="small" danger onClick={() => handleDeleteIp(record.id)}>Delete</Button>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2}>API Keys</Title>
        <Paragraph style={{ color: COLORS.textSecondary }}>
          Manage your RSA public key and IP whitelist for API access.
        </Paragraph>
        <Space style={{ marginTop: 8 }}>
          <Button
            type="link"
            icon={<BookOutlined />}
            href="https://solidstagingapi.switchwallet.io/documentation/merchant"
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: COLORS.accent,
              padding: 0,
              height: 'auto'
            }}
          >
            View API Documentation
          </Button>
        </Space>
      </HeaderContainer>

      <StyledCard
        title="Switch Wallet Public Key"
        extra={
          <Button
            icon={<KeyOutlined />}
            onClick={handleOpenSwitchWalletKeyModal}
          >
            View Key
          </Button>
        }
      >
        <Paragraph style={{ color: COLORS.textSecondary }}>
          View the Switch Wallet public key to verify webhook signatures. Available in both PEM and Base64 formats.
        </Paragraph>
      </StyledCard>

      <StyledCard
        title="Webhook Source IPs"
        extra={
          <Button
            icon={<CopyOutlined />}
            onClick={() => {
              navigator.clipboard.writeText(webhookIPs.join('\n'));
              message.success('Webhook IPs copied to clipboard');
            }}
            disabled={webhookIPs.length === 0}
          >
            Copy All
          </Button>
        }
      >
        <Paragraph style={{ color: COLORS.textSecondary, marginBottom: 16 }}>
          These are the IP addresses from which Switch Wallet sends webhooks. You can use this list to configure your firewall or security settings.
        </Paragraph>

        {webhookIPsLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        ) : webhookIPs.length > 0 ? (
          <div>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ color: COLORS.textPrimary }}>
                {webhookIPs.length} IP{webhookIPs.length !== 1 ? 's' : ''} configured
              </Text>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {webhookIPs.map((ip, index) => (
                <Tag
                  key={index}
                  style={{
                    background: COLORS.bgPrimary,
                    borderColor: COLORS.border,
                    color: COLORS.textPrimary,
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    padding: '4px 8px',
                    margin: 0,
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(ip);
                    message.success(`IP ${ip} copied to clipboard`);
                  }}
                  className="clickable-tag"
                >
                  {ip}
                </Tag>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <Text style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                Click any IP to copy it to your clipboard
              </Text>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: COLORS.textSecondary }}>
            <Text>No webhook IPs configured yet. Contact Switch Wallet support if you need this information.</Text>
          </div>
        )}
      </StyledCard>

      <StyledCard
        title="My API RSA Public Key"
        extra={
          <Space>
            {publicKey && (
              <>
                <Button
                  icon={<KeyOutlined />}
                  onClick={() => setMyApiKeyModalVisible(true)}
                >
                  View Key
                </Button>
                <Button danger onClick={handleRemoveKey}>
                  Remove Key
                </Button>
              </>
            )}
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setUploadModalVisible(true)}
            >
              {publicKey ? 'Update Key' : 'Upload Key'}
            </Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${COLORS.border}` }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Merchant Company App Id</Text>
              {merchantCompanyId ? (
                <>
                  <Space>
                    <Text 
                      copyable={{ 
                        text: merchantCompanyId,
                        tooltips: ['Copy', 'Copied!']
                      }}
                      style={{ 
                        fontFamily: 'monospace', 
                        fontSize: '14px',
                        color: COLORS.textPrimary,
                        wordBreak: 'break-all'
                      }}
                    >
                      {merchantCompanyId}
                    </Text>
                  </Space>
                  <Text style={{ color: COLORS.textSecondary, fontSize: '11px', fontStyle: 'italic' }}>
                    Use this identifier in the Authorization header for merchant API requests (Bearer {merchantCompanyId})
                  </Text>
                </>
              ) : (
                <Text style={{ color: COLORS.textSecondary, fontSize: '12px', fontStyle: 'italic' }}>
                  Loading...
                </Text>
              )}
            </Space>
            {apiKey && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text style={{ color: COLORS.textSecondary, fontSize: '12px' }}>API Key (x-api-key)</Text>
                <Space>
                  <Text 
                    copyable={{ 
                      text: apiKey,
                      tooltips: ['Copy', 'Copied!']
                    }}
                    style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '14px',
                      color: COLORS.textPrimary,
                      wordBreak: 'break-all'
                    }}
                  >
                    {apiKey}
                  </Text>
                </Space>
                <Text style={{ color: COLORS.textSecondary, fontSize: '11px', fontStyle: 'italic' }}>
                  Use this key in the x-api-key header for all merchant API requests
                </Text>
                <Button
                  type="link"
                  icon={<BookOutlined />}
                  href="https://solidstagingapi.switchwallet.io/documentation/merchant"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    color: COLORS.accent,
                    padding: 0,
                    height: 'auto',
                    fontSize: '11px'
                  }}
                >
                  View API Documentation
                </Button>
              </Space>
            )}
          </Space>
        </div>
        {loading ? (
          <Spin />
        ) : publicKey ? (
          <Paragraph style={{ color: COLORS.textSecondary }}>
            RSA public key is configured. Click "View Key" to see and copy your public key.
          </Paragraph>
        ) : (
          <Paragraph style={{ color: COLORS.textSecondary }}>
            No RSA public key uploaded. Upload a key to enable API access.
          </Paragraph>
        )}
      </StyledCard>

      <StyledCard
        title="IP Whitelist"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddIp}>
            Add IP Address
          </Button>
        }
      >
        <Table
          columns={ipColumns}
          dataSource={ipWhitelist}
          rowKey="id"
          pagination={false}
          style={{ background: 'transparent' }}
        />
      </StyledCard>

      <Modal
        title="Upload RSA Public Key"
        open={uploadModalVisible}
        onOk={handleUploadKey}
        onCancel={() => {
          setUploadModalVisible(false);
          uploadForm.resetFields();
        }}
        okText="Upload"
        width={600}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        <Form form={uploadForm} layout="vertical">
          <Form.Item
            name="publicKey"
            label="RSA Public Key"
            rules={[{ required: true, message: 'Please enter the RSA public key' }]}
          >
            <TextArea
              rows={8}
              placeholder="-----BEGIN PUBLIC KEY-----&#10;...&#10;-----END PUBLIC KEY-----"
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingIp ? 'Edit IP Address' : 'Add IP Address'}
        open={ipModalVisible}
        onOk={handleSaveIp}
        onCancel={() => {
          setIpModalVisible(false);
          ipForm.resetFields();
        }}
        okText={editingIp ? 'Update' : 'Add'}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        <Form form={ipForm} layout="vertical">
          <Form.Item
            name="ipAddress"
            label="IP Address"
            rules={[
              { required: true, message: 'Please enter an IP address' },
              { 
                pattern: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(?:\/(?:[0-9]|[1-2][0-9]|3[0-2]))?$/, 
                message: 'Please enter a valid IP address or CIDR block (e.g., 192.168.1.1 or 192.168.1.0/24)' 
              },
            ]}
          >
            <Input placeholder="192.168.1.1 or 192.168.1.0/24" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <Input placeholder="Optional description" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Switch Wallet Public Key"
        open={switchWalletKeyModalVisible}
        onCancel={() => setSwitchWalletKeyModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setSwitchWalletKeyModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="copy-pem"
            icon={<CopyOutlined />}
            onClick={() => {
              if (switchWalletPublicKey) {
                navigator.clipboard.writeText(switchWalletPublicKey);
                message.success('Switch Wallet public key (PEM) copied to clipboard');
              }
            }}
            disabled={!switchWalletPublicKey}
          >
            Copy PEM
          </Button>,
          <Button
            key="copy-base64"
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => {
              if (switchWalletPublicKey) {
                const base64Key = pemToBase64(switchWalletPublicKey);
                navigator.clipboard.writeText(base64Key);
                message.success('Switch Wallet public key (Base64) copied to clipboard');
              }
            }}
            disabled={!switchWalletPublicKey}
          >
            Copy Base64
          </Button>,
        ]}
        width={800}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        {switchWalletKeyLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        ) : switchWalletPublicKey ? (
          <div>
            <Paragraph style={{ color: COLORS.textSecondary, marginBottom: 16 }}>
              Use this public key to verify webhook signatures from Switch Wallet.
            </Paragraph>

            {/* Base64 Format Section */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: COLORS.textPrimary }}>Base64 Format (Single Line)</Text>
                <Text style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                  {pemToBase64(switchWalletPublicKey).length} characters
                </Text>
              </div>
              <div style={{
                background: COLORS.bgPrimary,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '4px',
                padding: '12px',
                wordBreak: 'break-all',
                fontFamily: 'monospace',
                fontSize: '13px',
                color: COLORS.textPrimary,
                maxHeight: '60px',
                overflowY: 'auto'
              }}>
                {pemToBase64(switchWalletPublicKey)}
              </div>
            </div>

            {/* PEM Format Section */}
            <div>
              <div style={{ marginBottom: 8 }}>
                <Text strong style={{ color: COLORS.textPrimary }}>PEM Format</Text>
              </div>
              <TextArea
                value={switchWalletPublicKey}
                readOnly
                rows={8}
                style={{
                  background: COLORS.bgPrimary,
                  color: COLORS.textPrimary,
                  borderColor: COLORS.border,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  wordBreak: 'break-all',
                }}
              />
            </div>
          </div>
        ) : (
          <Paragraph style={{ color: COLORS.textSecondary }}>
            Switch Wallet public key not available.
          </Paragraph>
        )}
      </Modal>

      <Modal
        title="My API RSA Public Key"
        open={myApiKeyModalVisible}
        onCancel={() => setMyApiKeyModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setMyApiKeyModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => {
              if (publicKey) {
                navigator.clipboard.writeText(publicKey);
                message.success('Public key copied to clipboard');
              }
            }}
            disabled={!publicKey}
          >
            Copy Key
          </Button>,
        ]}
        width={700}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        ) : publicKey ? (
          <div>
            <Paragraph style={{ color: COLORS.textSecondary, marginBottom: 16 }}>
              This is your RSA public key used for authenticating API requests to Switch Wallet.
            </Paragraph>
            <TextArea
              value={publicKey}
              readOnly
              rows={10}
              style={{
                background: COLORS.bgPrimary,
                color: COLORS.textPrimary,
                borderColor: COLORS.border,
                fontFamily: 'monospace',
                fontSize: '12px',
                wordBreak: 'break-all',
              }}
            />
          </div>
        ) : (
          <Paragraph style={{ color: COLORS.textSecondary }}>
            No RSA public key uploaded. Upload a key to enable API access.
          </Paragraph>
        )}
      </Modal>
    </PageContainer>
  );
};

export default ApiKeys;

